/**
 * /rate-sheets — Rate sheet list.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { rateSheetsListQueryOptions } from "@/lib/queries";

export const Route = createFileRoute("/_auth/rate-sheets/")({
  component: RateSheetsIndexPage,
});

const TYPE_CHIP: Record<string, "default" | "primary" | "secondary"> = {
  stipulated: "primary",
  cost_plus:  "secondary",
  hybrid:     "default",
};

function RateSheetsIndexPage() {
  const navigate = useNavigate();
  const sheetsQ = useQuery(rateSheetsListQueryOptions());
  const sheets = sheetsQ.data?.items ?? [];
  const total = sheetsQ.data?.total ?? 0;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Rate Sheets</h1>
            <span className="text-xs text-slate-500">{total} total</span>
          </div>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate({ to: "/rate-sheets/new" })}
          >
            New Rate Sheet
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {sheetsQ.isLoading ? (
          <div className="p-6 text-slate-500">Loading…</div>
        ) : sheets.length === 0 ? (
          <div className="p-6 text-slate-500">No rate sheets yet.</div>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Effective From</TableCell>
                <TableCell>Effective To</TableCell>
                <TableCell align="center">Labor Rows</TableCell>
                <TableCell align="center">Material Rows</TableCell>
                <TableCell align="center">Equipment Rows</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sheets.map((s) => (
                <TableRow
                  key={s.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate({ to: "/rate-sheets/$rateSheetId", params: { rateSheetId: s.id } })
                  }
                >
                  <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={s.rate_type.replace("_", "-")}
                      color={TYPE_CHIP[s.rate_type] ?? "default"}
                      size="small"
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                  <TableCell>{s.project_id ?? "Entity-wide"}</TableCell>
                  <TableCell>{s.effective_from}</TableCell>
                  <TableCell>{s.effective_to ?? "Open"}</TableCell>
                  <TableCell align="center">{s.labor.length}</TableCell>
                  <TableCell align="center">{s.materials.length}</TableCell>
                  <TableCell align="center">{s.equipment.length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
